import React, { useState, useEffect, useCallback } from 'react';
import { Pencil, Plus, HelpCircle } from 'lucide-react';
import './LookupManager.css';

function getPageNumbers(current, total) {
  const delta = 1;
  const pages = [];
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      pages.push(i);
    }
  }
  const withDots = [];
  let prev;
  for (const p of pages) {
    if (prev !== undefined) {
      if (p - prev === 2) withDots.push(prev + 1);
      else if (p - prev > 2) withDots.push('…');
    }
    withDots.push(p);
    prev = p;
  }
  return withDots;
}

const LookupManager = ({ title, subtitle, singularLabel, searchPlaceholder, fetchList, createItem, updateItem }) => {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);

  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState('');
  const [addError, setAddError] = useState('');
  const [adding, setAdding] = useState(false);

  const [editingItem, setEditingItem] = useState(null);
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [confirmToggle, setConfirmToggle] = useState(null); // the item whose status is pending confirmation
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback((p) => {
    setLoading(true);
    fetchList({ page: p, limit: 10, search: debounced || undefined })
      .then(r => {
        setItems(r.data?.data || []);
        setPagination(r.data?.pagination || {});
        setPage(p);
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, [fetchList, debounced]);

  useEffect(() => { load(1); }, [debounced, load]);

  const totalPages = pagination.totalPages || 1;

  // ── Add ─────────────────────────────────────────────────────
  const openAdd = () => { setAddName(''); setAddError(''); setShowAdd(true); };

  const handleAdd = async (e) => {
    e.preventDefault();
    setAddError('');
    setAdding(true);
    try {
      await createItem({ name: addName });
      setShowAdd(false);
      load(1);
    } catch (err) {
      setAddError(err.response?.data?.error || 'Failed to add');
    } finally {
      setAdding(false);
    }
  };

  const openEdit = (item) => { setEditingItem(item); setEditName(item.name || ''); setEditError(''); };

  const runToggle = async () => {
    if (!confirmToggle) return;
    setToggling(true);
    try {
      const nextActive = confirmToggle.is_active === false;
      const { data } = await updateItem(confirmToggle.id, { is_active: nextActive });
      setItems(list => list.map(i => i.id === confirmToggle.id ? data : i));
      setConfirmToggle(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update status');
    } finally {
      setToggling(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditError('');
    setSavingEdit(true);
    try {
      const { data } = await updateItem(editingItem.id, { name: editName });
      setItems(list => list.map(i => i.id === editingItem.id ? data : i));
      setEditingItem(null);
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update');
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="admin-lookup-page">
      <h1 className="admin-page-title">{title}</h1>
      <p className="admin-page-subtitle">{subtitle}</p>

      <div className="admin-lookup-toolbar">
        <input
          className="admin-search-input"
          placeholder={searchPlaceholder}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button className="admin-btn-primary" onClick={openAdd}>
          <Plus size={15} /> Add {singularLabel}
        </button>
      </div>

      <div className="admin-table-card">
        <table className="admin-table">
          <thead>
            <tr><th>{singularLabel}</th><th>Status</th><th>Action</th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={3} className="admin-table-empty">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={3} className="admin-table-empty">No {singularLabel.toLowerCase()}s found.</td></tr>
            ) : (
              items.map(item => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>
                    <button
                      className={`admin-status-badge clickable ${item.is_active !== false ? 'active' : 'inactive'}`}
                      onClick={() => setConfirmToggle(item)}
                    >
                      {item.is_active !== false ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="admin-table-actions">
                    <button className="admin-icon-btn" title="Edit" onClick={() => openEdit(item)}>
                      <Pencil size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="pagination">
          <span className="pg-info">
            Showing {items.length > 0 ? (page - 1) * 10 + 1 : 0} to {Math.min(page * 10, pagination.total || 0)} of {pagination.total || 0} entries
          </span>
          <div className="pg-controls">
            <button className="pg-btn pg-btn-word" onClick={() => load(page - 1)} disabled={page === 1}>Previous</button>
            {getPageNumbers(page, totalPages).map((p, i) => (
              p === '…'
                ? <span key={`dots-${i}`} className="pg-dots">…</span>
                : <button key={p} className={`pg-btn ${page === p ? 'active' : ''}`} onClick={() => load(p)}>{p}</button>
            ))}
            <button className="pg-btn pg-btn-word" onClick={() => load(page + 1)} disabled={page === totalPages}>Next</button>
          </div>
        </div>
      </div>

      {/* Add modal */}
      {showAdd && (
        <div className="admin-modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Add {singularLabel}</h2>
              <button className="admin-modal-close" onClick={() => setShowAdd(false)}>✕</button>
            </div>
            <form onSubmit={handleAdd} className="admin-modal-body">
              {addError && <div className="admin-auth-error">{addError}</div>}
              <div className="admin-form-group">
                <label>{singularLabel} Name</label>
                <input placeholder={`Enter ${singularLabel} Name`} value={addName} onChange={e => setAddName(e.target.value)} required autoFocus />
              </div>
              <div className="admin-modal-actions">
                <button type="button" className="admin-btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
                <button type="submit" className="admin-btn-primary" disabled={adding}>{adding ? 'Adding…' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editingItem && (
        <div className="admin-modal-overlay" onClick={() => setEditingItem(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Edit {singularLabel}</h2>
              <button className="admin-modal-close" onClick={() => setEditingItem(null)}>✕</button>
            </div>
            <form onSubmit={handleEdit} className="admin-modal-body">
              {editError && <div className="admin-auth-error">{editError}</div>}
              <div className="admin-form-group">
                <label>{singularLabel} Name</label>
                <input value={editName} onChange={e => setEditName(e.target.value)} required autoFocus />
              </div>
              <div className="admin-modal-actions">
                <button type="button" className="admin-btn-secondary" onClick={() => setEditingItem(null)}>Cancel</button>
                <button type="submit" className="admin-btn-primary" disabled={savingEdit}>{savingEdit ? 'Updating…' : 'Update'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmToggle && (
        <div className="admin-modal-overlay" onClick={() => setConfirmToggle(null)}>
          <div className="admin-confirm-modal" onClick={e => e.stopPropagation()}>
            <HelpCircle size={44} className="admin-confirm-icon info" />
            <h3>Change status to {confirmToggle.is_active !== false ? 'Inactive' : 'Active'}?</h3>
            <div className="admin-confirm-actions">
              <button className="admin-btn-primary" onClick={runToggle} disabled={toggling}>
                {toggling ? 'Please wait…' : 'Yes, change it'}
              </button>
              <button className="admin-btn-secondary" onClick={() => setConfirmToggle(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LookupManager;